"use client"

import {useState, useEffect, useRef, useMemo} from "react"
import "./SpeakerText.css"
import {Button, Slider} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {createAudioContext} from "../../core/TextToSpeechUtilsPoly";
import MediaDevicesUtils from "../../utils/MediaDevicesUtils";
import {getCompactDeviceName} from "../../utils/DeviceDisplayNames";

// 24-note original bell melody (not from any known song), in Hz.
// It loops continuously until the user presses Stop.
const MELODY_FREQUENCIES = [
    392.00, 440.00, 493.88, 523.25, 587.33, 523.25, 493.88, 440.00,
    392.00, 493.88, 587.33, 659.25, 587.33, 523.25, 493.88, 440.00,
    523.25, 587.33, 659.25, 783.99, 659.25, 587.33, 523.25, 493.88
]
const NOTE_DURATION_MS = 320
// Pleasant bell/chime timbre: harmonic partials sound warmer than inharmonic metallic ringing.
const BELL_PARTIAL_RATIO_1 = 2.0
const BELL_PARTIAL_RATIO_2 = 3.0
const BELL_PARTIAL_GAIN_1 = 0.16
const BELL_PARTIAL_GAIN_2 = 0.06
const BELL_ATTACK_TIME = 0.01
const BELL_DECAY_END_GAIN = 0.006
const BELL_DECAY_TIME = 0.364
// Meter calibration for bell test-tone activity (pre-VCOD scaling).
const METER_ACTIVITY_REFERENCE = 0.45

interface SpeakerTestProps {
    speakerId: string
    /** 0-200, from parent (VCOD). Test tone plays at this level. */
    volume?: number
    onVolumeChange?: (volume: number) => void
    /** Render icon-only trigger for grouped test UI */
    iconOnly?: boolean
    /** Optional language name without country, shown before title */
    languageName?: string
}

export default function SpeakerTest(props: SpeakerTestProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [audioLevel, setAudioLevel] = useState(0)
    const [error, setError] = useState<string | null>(null)
    const [testVolume, setTestVolume] = useState<number>(props.volume ?? 100)

    const audioContext = useRef<AudioContext | null>(null)
    const oscillator = useRef<OscillatorNode | null>(null)
    const oscillator2 = useRef<OscillatorNode | null>(null)
    const oscillator3 = useRef<OscillatorNode | null>(null)
    const analyser = useRef<AnalyserNode | null>(null)
    const gainNode = useRef<GainNode | null>(null)
    const envelopeGain = useRef<GainNode | null>(null)
    const partialGain1 = useRef<GainNode | null>(null)
    const partialGain2 = useRef<GainNode | null>(null)
    const toneFilter = useRef<BiquadFilterNode | null>(null)
    const animationFrame = useRef<number | null>(null)
    const melodyIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const volumeRef = useRef<number>(props.volume ?? 100)
    volumeRef.current = props.volume ?? 100

    const speakerId = props.speakerId;

    const selectedOutputDeviceLabel = useMemo(() => {
        const outputDevices = MediaDevicesUtils.instance().getDevices("audiooutput");
        const selected = outputDevices.find(d => d.deviceId === speakerId);
        return selected ? getCompactDeviceName(selected.label) : "Unknown output device";
    }, [speakerId, isOpen]);

    // Keep the gain node in sync with the parent's volume (VCOD level).
    // Use setValueAtTime so the gain is applied on the audio thread.
    useEffect(() => {
        if (gainNode.current && audioContext.current) {
            const level = (props.volume ?? 100) / 100
            gainNode.current.gain.setValueAtTime(level, audioContext.current.currentTime)
        }
    }, [props.volume])

    // Mirror parent VCOD into dialog slider (bidirectional sync).
    useEffect(() => {
        setTestVolume(props.volume ?? 100)
    }, [props.volume])

    // Initialize / destroy audio context when dialog opens or closes
    useEffect(() => {
        if (isOpen && !audioContext.current) {
            try {
                audioContext.current = createAudioContext(speakerId)
                analyser.current = audioContext.current.createAnalyser()
                analyser.current.fftSize = 2048  // ~46ms window — stable RMS for a continuous tone
                gainNode.current = audioContext.current.createGain()
                gainNode.current.gain.value = volumeRef.current / 100

                // Bell envelope: per-note strike + decay (oscillators plug in from playTestSound)
                envelopeGain.current = audioContext.current.createGain()
                envelopeGain.current.gain.value = 0
                partialGain1.current = audioContext.current.createGain()
                partialGain1.current.gain.value = BELL_PARTIAL_GAIN_1
                partialGain2.current = audioContext.current.createGain()
                partialGain2.current.gain.value = BELL_PARTIAL_GAIN_2
                toneFilter.current = audioContext.current.createBiquadFilter()
                toneFilter.current.type = "lowpass"
                toneFilter.current.frequency.value = 3600
                toneFilter.current.Q.value = 0.7

                // Chain: osc1/2/3 -> (partials) -> envelope -> lowpass -> gain -> analyser -> destination
                envelopeGain.current.connect(toneFilter.current)
                toneFilter.current.connect(gainNode.current)
                partialGain1.current.connect(envelopeGain.current)
                partialGain2.current.connect(envelopeGain.current)
                gainNode.current.connect(analyser.current)
                analyser.current.connect(audioContext.current.destination)
            } catch (err) {
                setError("Could not initialize audio. Your browser may not support the Web Audio API.")
            }
        }

        function destroy() {
            if (melodyIntervalRef.current) {
                clearInterval(melodyIntervalRef.current)
                melodyIntervalRef.current = null
            }
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current)
                animationFrame.current = null
            }

            if (oscillator.current) {
                oscillator.current.stop()
                oscillator.current.disconnect()
                oscillator.current = null
            }
            if (oscillator2.current) {
                oscillator2.current.stop()
                oscillator2.current.disconnect()
                oscillator2.current = null
            }
            if (oscillator3.current) {
                oscillator3.current.stop()
                oscillator3.current.disconnect()
                oscillator3.current = null
            }

            if (envelopeGain.current) {
                envelopeGain.current.disconnect()
                envelopeGain.current = null
            }
            if (partialGain1.current) {
                partialGain1.current.disconnect()
                partialGain1.current = null
            }
            if (partialGain2.current) {
                partialGain2.current.disconnect()
                partialGain2.current = null
            }
            if (toneFilter.current) {
                toneFilter.current.disconnect()
                toneFilter.current = null
            }
            if (gainNode.current) {
                gainNode.current.disconnect()
                gainNode.current = null
            }

            if (analyser.current) {
                analyser.current.disconnect()
                analyser.current = null
            }

            if (audioContext.current && audioContext.current.state !== "closed") {
                audioContext.current.close()
                audioContext.current = null
            }
        }

        if (!isOpen) {
            destroy();
        }

        return () => {
            destroy();
        }
    }, [isOpen])

    const stopTestSound = () => {
        if (melodyIntervalRef.current) {
            clearInterval(melodyIntervalRef.current)
            melodyIntervalRef.current = null
        }
        if (oscillator.current) {
            oscillator.current.stop()
            oscillator.current.disconnect()
            oscillator.current = null
        }
        if (oscillator2.current) {
            oscillator2.current.stop()
            oscillator2.current.disconnect()
            oscillator2.current = null
        }
        if (oscillator3.current) {
            oscillator3.current.stop()
            oscillator3.current.disconnect()
            oscillator3.current = null
        }
        if (animationFrame.current) {
            cancelAnimationFrame(animationFrame.current)
            animationFrame.current = null
        }
        setIsPlaying(false)
        setAudioLevel(0)
    }

    const triggerBellEnvelope = (ctx: AudioContext) => {
        const env = envelopeGain.current
        if (!env) return
        const t = ctx.currentTime
        env.gain.cancelScheduledValues(t)
        // Harder reset each note to avoid vibrato/warble perception between notes.
        env.gain.setValueAtTime(0.0001, t)
        env.gain.linearRampToValueAtTime(0.9, t + BELL_ATTACK_TIME)
        env.gain.exponentialRampToValueAtTime(BELL_DECAY_END_GAIN, t + BELL_DECAY_TIME)
    }

    const playTestSound = async () => {
        if (!audioContext.current || !analyser.current || !gainNode.current || !envelopeGain.current || !partialGain1.current || !partialGain2.current) return

        if (audioContext.current.state === "suspended") {
            await audioContext.current.resume()
        }

        stopTestSound()

        const ctx = audioContext.current
        const gain = gainNode.current
        const env = envelopeGain.current
        const part1 = partialGain1.current
        const part2 = partialGain2.current

        // Ensure test sound plays at current VCOD (main Volume slider) level
        const vcodLevel = (props.volume ?? 100) / 100
        gain.gain.setValueAtTime(vcodLevel, ctx.currentTime)

        const f0 = MELODY_FREQUENCIES[0]
        oscillator.current = ctx.createOscillator()
        oscillator.current.type = "triangle"
        oscillator.current.frequency.setValueAtTime(f0, ctx.currentTime)
        oscillator.current.connect(env)
        oscillator.current.start()

        oscillator2.current = ctx.createOscillator()
        oscillator2.current.type = "sine"
        oscillator2.current.frequency.setValueAtTime(f0 * BELL_PARTIAL_RATIO_1, ctx.currentTime)
        oscillator2.current.connect(part1)
        oscillator2.current.start()
        oscillator3.current = ctx.createOscillator()
        oscillator3.current.type = "sine"
        oscillator3.current.frequency.setValueAtTime(f0 * BELL_PARTIAL_RATIO_2, ctx.currentTime)
        oscillator3.current.connect(part2)
        oscillator3.current.start()

        triggerBellEnvelope(ctx)

        let noteIndex = 0
        setIsPlaying(true)
        analyzeAudio()

        melodyIntervalRef.current = setInterval(() => {
            noteIndex = (noteIndex + 1) % MELODY_FREQUENCIES.length
            // Re-apply VCOD level every note so slider changes take effect during playback
            const vcod = volumeRef.current / 100
            gain.gain.setValueAtTime(vcod, ctx.currentTime)
            const f = MELODY_FREQUENCIES[noteIndex]
            if (oscillator.current) {
                oscillator.current.frequency.setValueAtTime(f, ctx.currentTime)
            }
            if (oscillator2.current) {
                oscillator2.current.frequency.setValueAtTime(f * BELL_PARTIAL_RATIO_1, ctx.currentTime)
            }
            if (oscillator3.current) {
                oscillator3.current.frequency.setValueAtTime(f * BELL_PARTIAL_RATIO_2, ctx.currentTime)
            }
            triggerBellEnvelope(ctx)
        }, NOTE_DURATION_MS)
    }

    const analyzeAudio = () => {
        if (!analyser.current) return

        // Use time-domain RMS from the post-gain analyser so meter follows
        // actual output amplitude from the test sound path.
        const dataArray = new Uint8Array(analyser.current.fftSize)

        const updateLevel = () => {
            analyser.current?.getByteTimeDomainData(dataArray)

            let sumSq = 0
            for (let i = 0; i < dataArray.length; i++) {
                const d = (dataArray[i] - 128) / 128
                sumSq += d * d
            }
            const rms = Math.sqrt(sumSq / dataArray.length)
            const peakLike = rms * Math.SQRT2
            const activity = Math.min(peakLike / METER_ACTIVITY_REFERENCE, 1)
            // Make meter reflect selected VCOD (0..200) while still pulsing with notes.
            const vcodScale = Math.max(0, Math.min(volumeRef.current / 200, 1))
            const meter = activity * vcodScale

            setAudioLevel(meter)
            animationFrame.current = requestAnimationFrame(updateLevel)
        }

        updateLevel()
    }

    const toggleDialog = () => {
        if (isOpen) {
            stopTestSound()
        }
        setIsOpen(!isOpen)
    }

    return (
        <div className="speaker-test-container">
            <Button
                className="base-button speaker-test-trigger"
                onClick={toggleDialog}
                startIcon={props.iconOnly ? undefined : <VolumeUpIcon className="speaker-test-icon" />}
                aria-label="Output device test"
            >
                {props.iconOnly ? <VolumeUpIcon className="speaker-test-icon"/> : "Test"}
            </Button>

            {isOpen && (
                <div className="speaker-test-dialog-backdrop">
                    <div className="speaker-test-dialog" role="dialog" aria-labelledby="speaker-test-title">
                        <div className="speaker-test-dialog-header">
                            <h2 id="speaker-test-title">{props.languageName ? `${props.languageName} - ` : ""}Output Device Test</h2>
                            <button className="speaker-test-close-button" onClick={toggleDialog} aria-label="Close">
                                ×
                            </button>
                        </div>

                        <div className="speaker-test-dialog-content">
                            {error ? (
                                <div className="speaker-test-error">{error}</div>
                            ) : (
                                <>
                                    <div className="speaker-test-device-info">
                                        <div className="speaker-test-device-label">Testing the following output device:</div>
                                        <div className="speaker-test-device-name">
                                            <VolumeUpIcon fontSize="small"/>
                                            <span>{selectedOutputDeviceLabel}</span>
                                        </div>
                                    </div>
                                    <div className="speaker-test-controls">
                                        <Button
                                            variant={isPlaying ? "outlined" : "contained"}
                                            color={isPlaying ? "error" : "primary"}
                                            onClick={isPlaying ? stopTestSound : playTestSound}
                                        >
                                            {isPlaying ? "Stop sound" : "Play test sound"}
                                        </Button>
                                    </div>
                                    <div className="speaker-test-volume-control">
                                        <div className="speaker-test-volume-label">Volume</div>
                                        <Slider
                                            aria-label="Test output volume"
                                            value={testVolume}
                                            min={0}
                                            max={200}
                                            step={10}
                                            onChange={(_, v) => {
                                                const val = v as number
                                                setTestVolume(val)
                                                if (gainNode.current && audioContext.current) {
                                                    gainNode.current.gain.setValueAtTime(val / 100, audioContext.current.currentTime)
                                                }
                                                props.onVolumeChange?.(val)
                                            }}
                                        />
                                    </div>
                                    <div className="speaker-test-level-container">
                                        <div className="speaker-test-level-label">Audio Level:</div>
                                        <div className="speaker-test-level-meter">
                                            <div className="speaker-test-level-fill"
                                                 style={{transform: `scaleX(${Math.max(0, Math.min(audioLevel, 1))})`}}></div>
                                        </div>
                                        <div className="speaker-test-level-value">{Math.round(audioLevel * 100)}%</div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="speaker-test-dialog-footer">
                            <Button variant="outlined" onClick={toggleDialog}>
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
